// frontend/components/HrManagerReportsTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchWorkSummaryReport } from "../features/hr/hrThunks.js";
import { CalendarDays, Clock, Users } from "lucide-react";
import "../styles/hr/hr-manager-reports.css";

const safeNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const getEmployeeNameFromSummary = (summary) => {
  const employee = summary.employee || summary.employee_profile || {};
  const user = employee.user || {};
  const fullName = ((user.first_name || "") + " " + (user.last_name || "")).trim();

  if (fullName) return fullName;
  if (user.username) return user.username;
  if (employee.name) return employee.name;

  const id = summary.employee_id ?? employee.id ?? summary.id ?? null;
  return id ? `Empleado #${id}` : "Empleado";
};

const HrManagerReportsTab = () => {
  const dispatch = useDispatch();

  // Rango por defecto: 1 del mes actual → hoy
  const today = new Date();
  const defaultEnd = today.toISOString().slice(0, 10);
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    .toISOString()
    .slice(0, 10);

  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const workSummaryReport = useSelector((state) => state.hr.workSummaryReport);
  const loadingWorkSummaryReport = useSelector(
    (state) => state.hr.loadingWorkSummaryReport
  );
  const errorWorkSummaryReport = useSelector(
    (state) => state.hr.errorWorkSummaryReport
  );

  const employeesSummaries = useMemo(() => {
    if (workSummaryReport && Array.isArray(workSummaryReport.employees)) {
      return workSummaryReport.employees;
    }
    return [];
  }, [workSummaryReport]);

  const sortedSummaries = useMemo(() => {
    return [...employeesSummaries].sort((a, b) => {
      const aHours = safeNumber(a.total_hours);
      const bHours = safeNumber(b.total_hours);
      return bHours - aHours;
    });
  }, [employeesSummaries]);

  const { totalHoursAll, totalDaysAll, avgPerEmployee } = useMemo(() => {
    if (employeesSummaries.length === 0) {
      return { totalHoursAll: 0, totalDaysAll: 0, avgPerEmployee: 0 };
    }

    let totalHours = 0;
    let totalDays = 0;

    employeesSummaries.forEach((item) => {
      const hours = safeNumber(item.total_hours);
      const days = safeNumber(item.days_with_entries ?? item.days_worked ?? item.total_days) || 0;
      totalHours += hours;
      totalDays += days;
    });

    const avgPerEmp = employeesSummaries.length > 0 ? totalHours / employeesSummaries.length : 0;

    return {
      totalHoursAll: totalHours,
      totalDaysAll: totalDays,
      avgPerEmployee: avgPerEmp,
    };
  }, [employeesSummaries]);

  const safeTotalHoursAll = safeNumber(totalHoursAll);
  const safeTotalDaysAll = safeNumber(totalDaysAll);
  const safeAvgPerEmployee = safeNumber(avgPerEmployee);

  useEffect(() => {
    if (hasLoadedOnce) return;
    if (!startDate || !endDate) return;

    setHasLoadedOnce(true);
    dispatch(fetchWorkSummaryReport({ startDate, endDate }));
  }, [dispatch, hasLoadedOnce, startDate, endDate]);

  const handleSearch = () => {
    if (!startDate || !endDate) return;
    dispatch(fetchWorkSummaryReport({ startDate, endDate }));
  };

  const rangeLabel =
    workSummaryReport?.start_date && workSummaryReport?.end_date
      ? `${workSummaryReport.start_date} → ${workSummaryReport.end_date}`
      : `${startDate} → ${endDate}`;

  return (
    <div className="hr-manager-tab hr-manager-reports-tab">
      {/* Header card */}
      <div className="hr-manager-reports-header-card">
        <div className="hr-manager-reports-header-left">
          <p className="hr-manager-reports-kicker">Reportes</p>
          <h2 className="hr-manager-reports-title">Resumen de horas por empleado</h2>
          <p className="hr-manager-reports-subtitle">
            Genera un resumen de horas trabajadas en un rango de fechas.
          </p>
        </div>

        <div className="hr-manager-reports-header-right">
          <div className="hr-manager-reports-range-pill">
            <CalendarDays className="hr-manager-reports-range-icon" aria-hidden="true" />
            <span className="hr-manager-reports-range-text">{rangeLabel}</span>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="hr-manager-reports-filters-card">
        <div className="hr-manager-reports-filter-row">
          <div className="hr-filter-date-field">
            <label>Desde</label>
            <input
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </div>

          <div className="hr-filter-date-field">
            <label>Hasta</label>
            <input
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </div>

          <button
            type="button"
            className="hr-button hr-button--primary"
            onClick={handleSearch}
            disabled={loadingWorkSummaryReport || !startDate || !endDate}
          >
            {loadingWorkSummaryReport ? "Generando..." : "Actualizar"}
          </button>
        </div>

        {errorWorkSummaryReport && (
          <div className="hr-error-banner">
            Error al cargar reporte: {errorWorkSummaryReport}
          </div>
        )}
      </div>

      {/* Métricas */}
      <div className="hr-manager-reports-metrics">
        <div className="hr-metric-card hr-metric-card--primary">
          <div className="hr-metric-top">
            <span className="hr-metric-label">Horas totales</span>
            <Clock className="hr-metric-icon" aria-hidden="true" />
          </div>
          <div className="hr-metric-value">
            {safeTotalHoursAll.toFixed(2)}
            <span className="hr-metric-unit">h</span>
          </div>
          <p className="hr-metric-helper">
            Suma de horas del equipo en el rango seleccionado.
          </p>
        </div>

        <div className="hr-metric-card">
          <div className="hr-metric-top">
            <span className="hr-metric-label">Empleados con actividad</span>
            <Users className="hr-metric-icon" aria-hidden="true" />
          </div>
          <div className="hr-metric-value">
            {employeesSummaries.length}
            <span className="hr-metric-unit">empleados</span>
          </div>
          <p className="hr-metric-helper">
            Empleados que tienen fichajes dentro del periodo.
          </p>
        </div>

        <div className="hr-metric-card">
          <div className="hr-metric-top">
            <span className="hr-metric-label">Media por empleado</span>
            <Clock className="hr-metric-icon" aria-hidden="true" />
          </div>
          <div className="hr-metric-value">
            {safeAvgPerEmployee.toFixed(2)}
            <span className="hr-metric-unit">h/empleado</span>
          </div>
          <p className="hr-metric-helper">
            Promedio de horas por empleado en el periodo.
          </p>
        </div>

        <div className="hr-metric-card">
          <div className="hr-metric-top">
            <span className="hr-metric-label">Días con fichajes</span>
            <CalendarDays className="hr-metric-icon" aria-hidden="true" />
          </div>
          <div className="hr-metric-value">
            {safeTotalDaysAll}
            <span className="hr-metric-unit">días</span>
          </div>
          <p className="hr-metric-helper">
            Total de días (sumados) con actividad registrada.
          </p>
        </div>
      </div>

      {/* Tabla */}
      <div className="hr-manager-reports-table-card">
        <div className="hr-manager-reports-table-header">
          <div>
            <h3 className="hr-manager-reports-table-title">Resumen por empleado</h3>
            <p className="hr-manager-reports-table-subtitle">
              Rango: <strong>{rangeLabel}</strong>
            </p>
          </div>

          <div className="hr-manager-reports-table-pill">
            <Clock className="hr-manager-reports-table-icon" aria-hidden="true" />
            <span>Detalle de horas y días</span>
          </div>
        </div>

        {loadingWorkSummaryReport && employeesSummaries.length === 0 ? (
          <div className="hr-manager-reports-empty">Generando reporte...</div>
        ) : employeesSummaries.length === 0 ? (
          <div className="hr-manager-reports-empty">
            No hay datos de fichajes en este rango de fechas.
          </div>
        ) : (
          <div className="hr-time-history-table-wrapper">
            <table className="hr-table">
              <thead>
                <tr>
                  <th>Empleado</th>
                  <th>Horas totales</th>
                  <th>Días con fichajes</th>
                  <th>Media h/día</th>
                </tr>
              </thead>
              <tbody>
                {sortedSummaries.map((item, index) => {
                  const totalHours = safeNumber(item.total_hours);
                  const daysWorked = safeNumber(
                    item.days_with_entries ?? item.days_worked ?? item.total_days
                  ) || 0;
                  const avgPerDay = daysWorked > 0 ? totalHours / daysWorked : 0;

                  const rowKey =
                    item.employee_id ?? item.id ?? `${getEmployeeNameFromSummary(item)}-${index}`;

                  return (
                    <tr key={rowKey}>
                      <td>{getEmployeeNameFromSummary(item)}</td>
                      <td>{totalHours.toFixed(2)}</td>
                      <td>{daysWorked}</td>
                      <td>{avgPerDay.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default HrManagerReportsTab;